import { useRef, useState } from "react";
import { Replayer } from "rrweb";
import { DEFAULT_BLOCKED_CLASS, DEFAULT_INACTIVE_PERIOD, getInactivePeriods, SESSION_PLAYER_SPEEDS } from "../utils";

export const useSetupSessionPlayer = () => {
  
  const [ isPlaying, setPlayerState ] = useState(false);
  
  const [ playTime, setPlayTime ] = useState(0);
  
  const [ inactivePeriods, setInactivePeriods ] = useState([]);
  const [ skipInactive, setSkipInactive ] = useState(true);
  const [ playSpeed, setSpeed ] = useState(SESSION_PLAYER_SPEEDS[0]);
  
  const playerRef = useRef(null);
  const playerContainerRef = useRef(null);
  
  const timerRef = useRef(null);
  const recordingTimeRef = useRef({ startTime: 0, endTime: 0, totalTime: 0 });
  
  const loopTimer = () => {
    stopTimer();
    const update = () => {
      const time = playerRef.current.getCurrentTime();
      setPlayTime(time < 0 ? 0 : time);
      
      if (time < recordingTimeRef.current.totalTime) {
        timerRef.current = requestAnimationFrame(update);
      }
    };
    timerRef.current = requestAnimationFrame(update);
  };
  
  const stopTimer = () => {
    if (timerRef.current) {
      cancelAnimationFrame(timerRef.current);
      timerRef.current = null;
    }
  };
  
  const setupPlayer = (session) => {
    playerRef.current = new Replayer(session, {
      root: playerContainerRef.current,
      skipInactive,
      blockClass: DEFAULT_BLOCKED_CLASS,
      inactivePeriodThreshold: DEFAULT_INACTIVE_PERIOD,
      speed: playSpeed,
      maxSpeed: SESSION_PLAYER_SPEEDS.slice(-1)[0] * 2,
    });
    
    setInactivePeriods(getInactivePeriods(session, DEFAULT_INACTIVE_PERIOD))
    recordingTimeRef.current = playerRef.current.getMetaData();
    
    playerRef.current.on("start", () => {
      setPlayerState(true);
      loopTimer();
    });
    playerRef.current.on("resume", () => {
      setPlayerState(true);
      loopTimer();
    });
    playerRef.current.on("pause", () => {
      setPlayerState(false);
      stopTimer();
    });
    playerRef.current.on("finish", () => {
      setPlayerState(false);
      stopTimer();
      setPlayTime(recordingTimeRef.current.totalTime);
    });
  }
  
  const cleanupPlayer = () => {
    if(!playerRef.current)
      return;
    
    pause();
    delete playerRef.current;
    if (playerContainerRef.current)
      playerContainerRef.current.innerHTML = "";
  }
  
  /** =============== CONTROLS =============== */
  
  const resume = () => {
    if (playTime === 0)
      playerRef.current.play(0);
    else if (playTime === recordingTimeRef.current.totalTime) {
      setPlayTime(0);
      playerRef.current.play(0);
    } else
      playerRef.current.resume(playTime);
  };
  
  const pause = () => {
    playerRef.current.pause(playTime);
  };
  
  const toggleInactivity = () => {
   playerRef.current.setConfig({ skipInactive: !skipInactive })
    setSkipInactive(!skipInactive);
  };
  
  const setPlaySpeed = (newSpeed) => {
    const needsPause = isPlaying;
    if(needsPause) pause();
    playerRef.current.setConfig({ speed: newSpeed })
    setSpeed(newSpeed);
    if(needsPause) resume();
  };
  
  const handleSlider = (event, newValue) => {
    if (isPlaying)
      pause();
    setPlayTime(newValue);
  };
  
  const handleSliderCommit = () => {
    resume();
  };
  
  return {
    playerRef,
    playerContainerRef,
    setupPlayer,
    cleanupPlayer,
    
    isPlaying,
    
    ...recordingTimeRef.current,
    playTime,
    inactivePeriods,
    skipInactive,
    playSpeed,
    
    resume,
    pause,
    toggleInactivity,
    setPlaySpeed,
    
    handleSlider,
    handleSliderCommit,
  }
}